import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { getAdminStats } from '../api/adminApi';
import { useAdminAuth } from '../context/AdminAuthContext';

function StatCard({ icon, title, value, color }) {
  return (
    <div
      className="w3-card-4 w3-round-large"
      style={{
        backgroundColor: '#263238',
        padding: '20px',
        flex: '1 1 220px',
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${color}33`
      }}
    >
      <div
        style={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          backgroundColor: `${color}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <i className="material-icons" style={{ fontSize: 26, color }}>
          {icon}
        </i>
      </div>
      <div style={{ marginRight: 14 }}>
        <div className="w3-text-white" style={{ fontSize: 24, fontWeight: 'bold' }}>
          {value}
        </div>
        <div className="w3-text-light-grey" style={{ fontSize: 13 }}>
          {title}
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fa-IR');
}

function formatShortDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' });
}

function formatNumber(num) {
  return new Intl.NumberFormat('fa-IR').format(num);
}

// رنگ‌های مورد استفاده تو نمودار دایره‌ای
const PIE_COLORS = ['#4FC3F7', '#15ff00', '#ff9800', '#e91e63', '#9c27b0', '#2980b9'];

// جعبه سفارشی برای Tooltip نمودارها (هماهنگ با تم تیره)
function CustomTooltip({ active, payload, label, suffix = '' }) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        backgroundColor: '#1c2529',
        border: '1px solid #37474F',
        borderRadius: 8,
        padding: '8px 12px',
        direction: 'rtl'
      }}
    >
      <p className="w3-text-white" style={{ margin: 0, fontSize: 12 }}>{label}</p>
      {payload.map((item, idx) => (
        <p key={idx} style={{ margin: 0, color: item.color || item.fill, fontSize: 13, fontWeight: 'bold' }}>
          {formatNumber(item.value)} {suffix}
        </p>
      ))}
    </div>
  );
}

export const AdminDashboard = () => {
  const { token } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadStats() {
      setLoading(true);
      const result = await getAdminStats(token);
      setLoading(false);

      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.message);
      }
    }
    loadStats();
  }, [token]);

  if (loading) {
    return (
      <div className="w3-text-white w3-center" style={{ padding: 60 }}>
        در حال بارگذاری آمار...
      </div>
    );
  }

  if (error) {
    return (
      <div className="w3-text-red w3-center" style={{ padding: 60 }}>
        {error}
      </div>
    );
  }

  // آماده‌سازی داده روند ثبت‌نام برای نمودار (تاریخ فرمت‌شده)
  const signupChartData = stats.userSignupTrend.map((row) => ({
    date: formatShortDate(row.date),
    count: row.count
  }));

  const revenueChartData = stats.revenueTrend.map((row) => ({
    date: formatShortDate(row.date),
    total: row.total
  }));

  const planPieData = stats.planBreakdown.map((p) => ({
    name: p.title,
    value: p.count
  }));

  return (
    <div dir="rtl" style={{ padding: 24 }}>
      <h2 className="w3-text-white" style={{ marginBottom: 20 }}>گزارش کلی کاربران</h2>

      {/* کارت‌های آماری */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 30 }}>
        <StatCard icon="group" title="کل کاربران ثبت‌نام‌شده" value={formatNumber(stats.totalUsers)} color="#4FC3F7" />
        <StatCard icon="verified_user" title="اعضای دارای عضویت فعال" value={formatNumber(stats.activeMembers)} color="#15ff00" />
        <StatCard icon="shopping_cart" title="کاربرانی که حداقل یک پلن خریده‌اند" value={formatNumber(stats.everPurchased)} color="#ff9800" />
        <StatCard icon="person_off" title="کاربران بدون هیچ خرید" value={formatNumber(stats.noPurchaseUsers)} color="#ff0000" />
        <StatCard icon="payments" title="مجموع درآمد (تومان)" value={formatNumber(stats.totalRevenue)} color="#e91e63" />
        <StatCard icon="check_circle" title="تراکنش‌های موفق" value={formatNumber(stats.paymentStats.success)} color="#15ff00" />
        <StatCard icon="cancel" title="تراکنش‌های ناموفق" value={formatNumber(stats.paymentStats.failed)} color="#ff0000" />
        <StatCard icon="hourglass_empty" title="تراکنش‌های در انتظار" value={formatNumber(stats.paymentStats.pending)} color="#ff9800" />
      </div>

      {/* نمودارها */}
      <div className="w3-row-padding" style={{ marginBottom: 24 }}>
        {/* نمودار روند ثبت‌نام */}
        <div className="w3-col l6 m12 s12" style={{ marginBottom: 16 }}>
          <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 20, height: 320 }}>
            <h4 className="w3-text-white" style={{ marginBottom: 14, display: 'flex', alignItems: 'center' }}>
              <i className="material-icons w3-margin-left" style={{ color: '#4FC3F7' }}>trending_up</i>
              روند ثبت‌نام کاربران (۳۰ روز اخیر)
            </h4>
            {signupChartData.length === 0 ? (
              <p className="w3-text-grey w3-center" style={{ paddingTop: 60 }}>داده‌ای برای نمایش وجود ندارد.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={signupChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#37474F" />
                  <XAxis dataKey="date" stroke="#90A4AE" fontSize={11} />
                  <YAxis stroke="#90A4AE" fontSize={11} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip suffix="کاربر" />} />
                  <Line type="monotone" dataKey="count" stroke="#4FC3F7" strokeWidth={2} dot={{ r: 3 }} name="ثبت‌نام" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* نمودار توزیع پلن‌ها */}
        <div className="w3-col l6 m12 s12" style={{ marginBottom: 16 }}>
          <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 20, height: 320 }}>
            <h4 className="w3-text-white" style={{ marginBottom: 14, display: 'flex', alignItems: 'center' }}>
              <i className="material-icons w3-margin-left" style={{ color: '#ff9800' }}>pie_chart</i>
              توزیع اعضای فعال بر اساس پلن
            </h4>
            {planPieData.length === 0 ? (
              <p className="w3-text-grey w3-center" style={{ paddingTop: 60 }}>عضویت فعالی وجود ندارد.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={planPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {planPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip suffix="نفر" />} />
                  <Legend
                    wrapperStyle={{ fontSize: 12, color: 'white' }}
                    formatter={(value) => <span style={{ color: 'white' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* نمودار درآمد روزانه */}
      <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 20, marginBottom: 24, height: 320 }}>
        <h4 className="w3-text-white" style={{ marginBottom: 14, display: 'flex', alignItems: 'center' }}>
          <i className="material-icons w3-margin-left" style={{ color: '#15ff00' }}>bar_chart</i>
          روند درآمد روزانه (۳۰ روز اخیر - تومان)
        </h4>
        {revenueChartData.length === 0 ? (
          <p className="w3-text-grey w3-center" style={{ paddingTop: 60 }}>درآمدی برای نمایش وجود ندارد.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenueChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#37474F" />
              <XAxis dataKey="date" stroke="#90A4AE" fontSize={11} />
              <YAxis stroke="#90A4AE" fontSize={11} />
              <Tooltip content={<CustomTooltip suffix="تومان" />} />
              <Bar dataKey="total" fill="#15ff00" radius={[6, 6, 0, 0]} name="درآمد" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* تفکیک اعداد پلن‌ها (نسخه جدولی، در کنار نمودار دایره‌ای بالا) */}
      <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 20, marginBottom: 24 }}>
        <h4 className="w3-text-white" style={{ marginBottom: 14 }}>تفکیک اعضای فعال بر اساس نوع پلن</h4>
        {stats.planBreakdown.length === 0 ? (
          <p className="w3-text-grey">هیچ عضویت فعالی وجود ندارد.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {stats.planBreakdown.map((plan, idx) => (
              <div
                key={idx}
                className="w3-round-large"
                style={{
                  backgroundColor: '#1c2529',
                  border: `1px solid ${PIE_COLORS[idx % PIE_COLORS.length]}55`,
                  padding: '10px 16px',
                  minWidth: 140
                }}
              >
                <div className="w3-text-white" style={{ fontSize: 14 }}>{plan.title}</div>
                <div style={{ color: PIE_COLORS[idx % PIE_COLORS.length], fontSize: 20, fontWeight: 'bold' }}>
                  {formatNumber(plan.count)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* آخرین کاربران ثبت‌نام‌شده */}
      <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 20, marginBottom: 24 }}>
        <h4 className="w3-text-white" style={{ marginBottom: 14 }}>آخرین کاربران ثبت‌نام‌شده</h4>
        {stats.recentUsers.length === 0 ? (
          <p className="w3-text-grey">کاربری ثبت‌نام نکرده است.</p>
        ) : (
          <table className="w3-table w3-text-white" style={{ width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #37474F' }}>
                <th style={{ textAlign: 'right', padding: 8 }}>نام کاربری</th>
                <th style={{ textAlign: 'right', padding: 8 }}>شماره تماس</th>
                <th style={{ textAlign: 'right', padding: 8 }}>تاریخ ثبت‌نام</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentUsers.map((u, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #263238' }}>
                  <td style={{ padding: 8 }}>{u.account_name}</td>
                  <td style={{ padding: 8 }}>{u.phone_number}</td>
                  <td style={{ padding: 8 }}>{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* آخرین خریدها */}
      <div className="w3-card-4 w3-round-large" style={{ backgroundColor: '#263238', padding: 20 }}>
        <h4 className="w3-text-white" style={{ marginBottom: 14 }}>آخرین خریدهای موفق</h4>
        {stats.recentPurchases.length === 0 ? (
          <p className="w3-text-grey">هنوز خریدی ثبت نشده است.</p>
        ) : (
          <table className="w3-table w3-text-white" style={{ width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #37474F' }}>
                <th style={{ textAlign: 'right', padding: 8 }}>کاربر</th>
                <th style={{ textAlign: 'right', padding: 8 }}>پلن</th>
                <th style={{ textAlign: 'right', padding: 8 }}>مبلغ (تومان)</th>
                <th style={{ textAlign: 'right', padding: 8 }}>تاریخ</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentPurchases.map((p, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #263238' }}>
                  <td style={{ padding: 8 }}>{p.account_name}</td>
                  <td style={{ padding: 8 }}>{p.plan_title}</td>
                  <td style={{ padding: 8 }}>{formatNumber(p.amount)}</td>
                  <td style={{ padding: 8 }}>{formatDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};